import { frameToRelativeSeconds } from 'utils/media-time';
import { config_V2, toDisplay_V2 } from '../phase2-annotation-conf';
import { computeTranscriptCheckpointFrames } from '../standard-workspace/transcript-checkpoints';
import {
    PHASE4A_SOURCE_ATTRIBUTES,
    PHASE4B_SOURCE_ATTRIBUTES,
    selectedQuestionDependencyIsAvailable,
} from './manifest';

interface CompletionOptions {
    phase: string;
    people: any[];
    transcriptData: any;
    startFrame: number;
    stopFrame: number;
    frameRate: number;
    jobAttributeSpec: any[];
    jobAttributes: Record<number, string>;
}

function recurringValue(rawValue: string, frame: number): string {
    try {
        return rawValue ? JSON.parse(rawValue)[frame] ?? '' : '';
    } catch (_error) {
        return '';
    }
}

export function isTranscriptReviewComplete(options: CompletionOptions): boolean {
    const {
        phase,
        people,
        transcriptData,
        startFrame,
        stopFrame,
        frameRate,
        jobAttributeSpec,
        jobAttributes,
    } = options;

    if (!transcriptData) {
        return false;
    }

    const phase4a = phase === 'phase4a';
    const allowedAttributes = phase4a ?
        PHASE4A_SOURCE_ATTRIBUTES :
        PHASE4B_SOURCE_ATTRIBUTES;
    const checkpointFrames = computeTranscriptCheckpointFrames({
        mode: phase4a ? 'phase2a' : 'phase2b',
        transcriptData,
        people,
        startFrame,
        stopFrame,
        frameRate,
    });
    const recurringCount = phase4a ? 0 : Math.max(0, checkpointFrames.length - 1);
    const stageCount = phase4a ? checkpointFrames.length + 1 : recurringCount + 1;

    const stagePrefix = (stage: number): string => {
        if (phase4a) {
            return stage < checkpointFrames.length ? `phase2a.popup.${stage}.` : 'phase2a.sidebar.';
        }
        return stage < recurringCount ? 'phase2b.popup.recurring.' : 'phase2b.popup.0.';
    };
    const stageFrame = (stage: number): number => {
        if (phase4a) {
            return stage < checkpointFrames.length ? checkpointFrames[stage] : stopFrame - 1;
        }
        return stage < recurringCount ? checkpointFrames[stage] : stopFrame - 1;
    };
    const personAppears = (person: any, stage: number): boolean => {
        const finalStage = stage === stageCount - 1;
        const previousFrame = stage > 0 ?
            checkpointFrames[Math.min(stage - 1, checkpointFrames.length - 1)] :
            startFrame;
        const windowStart = frameToRelativeSeconds(previousFrame, startFrame, frameRate);
        const windowEnd = frameToRelativeSeconds(stageFrame(stage), startFrame, frameRate);
        return transcriptData.segments?.some((segment: any) => {
            const [, , serverID] = (segment.speaker || '').split('_');
            const inWindow = finalStage ?
                segment.start <= windowEnd :
                segment.start >= windowStart && segment.start <= windowEnd;
            return `${person.serverID}` === serverID && inWindow;
        }) || false;
    };

    return Array.from({ length: stageCount }, (_value, stage) => {
        const prefix = stagePrefix(stage);
        const peopleComplete = people.every((person) => {
            const attributes = person.label.attributes.filter((attribute: any) => (
                attribute.name.startsWith(prefix) &&
                allowedAttributes.has(attribute.name) &&
                (
                    !selectedQuestionDependencyIsAvailable(
                        allowedAttributes,
                        config_V2[attribute.name]?.followupParent,
                    ) ||
                    toDisplay_V2(
                        person.label.attributes,
                        jobAttributeSpec,
                        person.attributes,
                        jobAttributes,
                        attribute,
                        people,
                    )
                )
            ));
            if (!attributes.length || !personAppears(person, stage)) {
                return true;
            }
            return attributes.every((attribute: any) => {
                const rawValue = person.attributes[attribute.id] || '';
                return phase4a || stage >= recurringCount ?
                    rawValue !== '' :
                    recurringValue(rawValue, stageFrame(stage)) !== '';
            });
        });
        const jobAttributesComplete = jobAttributeSpec
            .filter((attribute: any) => (
                attribute.name.startsWith(prefix) &&
                allowedAttributes.has(attribute.name)
            ))
            .every((attribute: any) => (jobAttributes[attribute.id] || '') !== '');
        return peopleComplete && jobAttributesComplete;
    }).every(Boolean);
}
